import React, { useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Flex, Form, Row, Col } from "antd"
import { CustomInput } from "ui/Input"
import { CustomForm } from "ui/Form"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import useFormErrors from "hooks/useAntdFormErrors"
import { CountrySelector } from "ui/Input/CountrySelector"
import useValidateAddress from "modules/google/hooks/useValidateAddress"
import useGeocoder from "modules/google/hooks/useGeocoder"
import Spinner from "components/common/Spinner"
import { Confirmed } from "ui/Icon/DSIcon/Confirmed"

export const CustomAddress = ({ backToSearch, form, initialValues, setCenterLatLng, setMeetingLatLng }) => {
  const { t } = useTranslation()
  const requiredErrorMessage = t("form_validation.cannot_be_blank")
  const confirmAddressLabel = t("listings.tabs.location.confirm_address")
  const confirmedLabel = t("listings.tabs.location.confirmed")
  const geocoder = useGeocoder()
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [buttonLabel, setButtonLabel] = useState(confirmAddressLabel)
  useFormErrors(form)

  const selectedCountry = Form.useWatch("country", form)
  const isZipRequired = selectedCountry !== "BS"

  const { handleConfirmAddress, loading, errorMessage, suggestedAddress, suggestedComponents, clearError } = useValidateAddress(
    geocoder,
    form,
    setCenterLatLng,
    setMeetingLatLng,
    setIsButtonDisabled,
    setButtonLabel
  )

  const handleValuesChange = () => {
    setIsButtonDisabled(false)
    setButtonLabel(confirmAddressLabel)
  }

  const handleUseSuggestedAddress = () => {
    if (suggestedComponents) {
      form.setFieldsValue(suggestedComponents)
      setIsButtonDisabled(false)
      clearError()
    }
  }

  const ADDRESS_FIELDS = ["street_address", "city", "state", "zip_code", "country"]

  const handleConfirmClick = async () => {
    try {
      await form.validateFields(ADDRESS_FIELDS)
      setButtonLabel(t("listings.tabs.location.confirming"))
      const response = await handleConfirmAddress()
      if (response) {
        setIsButtonDisabled(true)
        setButtonLabel(confirmedLabel)
      } else {
        setButtonLabel(confirmAddressLabel)
      }
    } catch {
      setButtonLabel(confirmAddressLabel)
    }
  }

  const renderButtonLabel = () => {
    if (buttonLabel === confirmedLabel) {
      return (
        <Flex align="center" justify="center">
          <Confirmed className="mr-s" />
          {buttonLabel}
        </Flex>
      )
    }
    return buttonLabel
  }

  return (
    <>
      <Text className="align-center mb-l" variant="link" onClick={backToSearch}>
        <Icon type="feather" name="ArrowLeft" className="mr-xs" width={16} height={16} /> {t("pro.addresses.back_to_search")}
      </Text>
      <CustomForm form={form} layout="vertical" initialValues={initialValues} onValuesChange={handleValuesChange}>
        <Form.Item
          label={t("pro.addresses.street_address")}
          name="street_address"
          required={true}
          rules={[{ required: true, message: requiredErrorMessage }]}
        >
          <CustomInput />
        </Form.Item>

        <Row gutter={16}>
          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label={t("pro.addresses.city")}
              name="city"
              required={true}
              rules={[{ required: true, message: requiredErrorMessage }]}
            >
              <CustomInput />
            </Form.Item>
          </Col>

          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label={t("pro.addresses.state")}
              name="state"
              required={true}
              rules={[{ required: true, message: requiredErrorMessage }]}
            >
              <CustomInput />
            </Form.Item>
          </Col>

          <Col md={8} sm={24} xs={24}>
            <Form.Item
              label={t("pro.addresses.zip")}
              name="zip_code"
              required={isZipRequired}
              rules={isZipRequired ? [{ required: true, message: requiredErrorMessage }] : []}
            >
              <CustomInput />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={t("pro.addresses.country")}
          name="country"
          required={true}
          rules={[{ required: true, message: requiredErrorMessage }]}
        >
          <CountrySelector />
        </Form.Item>
        <Form.Item name="latitude" hidden><CustomInput /></Form.Item>
        <Form.Item name="longitude" hidden><CustomInput /></Form.Item>

        {errorMessage && (
          <div className="mb-m">
            <Alert message={errorMessage} type="error" showIcon closable />
            {suggestedAddress && (
              <Flex align="center" justify="space-between" style={{ marginTop: "8px" }}>
                <div style={{ fontSize: "14px", flex: 1 }}>
                  <strong>{t("listings.tabs.location.suggested_address")}:</strong> {suggestedAddress}
                </div>
                {suggestedComponents && (
                  <Button type="link" size="small" onClick={handleUseSuggestedAddress} style={{ marginLeft: "8px" }}>
                    {t("listings.tabs.location.use_this_address")}
                  </Button>
                )}
              </Flex>
            )}
          </div>
        )}

        <Button type="primary" size="large" className="px-6" disabled={isButtonDisabled || loading} onClick={handleConfirmClick}>
          {loading ? (
            <Flex>
              <Spinner className="mr-s" color="white" size="small" />
              {t("listings.tabs.location.confirming")}
            </Flex>
          ) : (
            renderButtonLabel()
          )}
        </Button>

        {buttonLabel === confirmedLabel && (
          <div style={{ marginTop: "12px", color: "var(--color-primary)", fontSize: "14px" }}>
            {t("listings.tabs.location.verify_location_on_map")}
          </div>
        )}
      </CustomForm>
    </>
  )
}

CustomAddress.propTypes = {
  backToSearch: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  setCenterLatLng: PropTypes.func,
  setMeetingLatLng: PropTypes.func
}
